import type { ComponentType } from "react";
import {
  ShieldIcon, BookIcon, TransportIcon, MessageSquareIcon,
  MealIcon, SparkleIcon, DocumentIcon, ReceiptIcon,
} from "@/components/ui/MokaIcons";

const GOLD = "#B8893C";
const GREEN = "#0E3B36";

export type ProductDetail = {
  id: string;
  name: string;
  shortName: string;
  category: string;
  tagline: string;
  description: string;
  icon: ComponentType<{ accent?: string; className?: string; style?: React.CSSProperties }>;
  color: string;
  gradient: string;
  capabilities: string[];
  powers?: string[];
  supports?: string[];
  knows?: string[];
  principles?: string[];
  aiCanAnswer?: string[];
  metric: { value: string; label: string };
  unique: string;
  stats: { value: string; label: string }[];
  visuals: {
    type: "identity" | "learn" | "transit" | "connect" | "campus" | "intelligence" | "government" | "pay";
  };
};

export const products: ProductDetail[] = [
  {
    id: "identity",
    name: "MOKA Identity",
    shortName: "Identity",
    category: "Foundation",
    tagline: "Digital identity for every learner. The trusted identity layer for African education.",
    description:
      "Identity is the foundation of every interaction within the MOKA ecosystem. Every student, parent, teacher, staff member, alumni, contractor, visitor, and institution is assigned a persistent digital identity that follows them throughout their educational journey. Rather than relying on fragmented admission numbers or temporary school IDs, MOKA Identity creates a secure, lifelong education identity that powers authentication, permissions, records, and services across the platform.",
    icon: ShieldIcon,
    color: GREEN,
    gradient: "linear-gradient(135deg, #0E3B36, #238B3F)",
    capabilities: [
      "Student Identity Management",
      "Parent & Guardian Identity",
      "Teacher & Staff Identity",
      "Alumni Identity",
      "Visitor & Guest Identity",
      "Institutional Identity",
      "Digital ID Cards",
      "QR & NFC Credentials",
      "Biometric Authentication",
      "Single Sign-On (SSO)",
      "Role & Permission Management",
      "Household Relationships",
      "Multi-school Enrollment",
      "Digital Student Portfolio",
      "Academic Record Ownership",
      "Consent & Privacy Controls",
      "Identity Verification",
      "Secure Document Vault",
      "API Identity Services",
    ],
    powers: [
      "Admissions",
      "Attendance",
      "Examinations",
      "Payments",
      "Library",
      "Transport",
      "Cafeteria",
      "Healthcare",
      "Communication",
      "Government reporting",
    ],
    metric: { value: "100%", label: "Verified enrollment" },
    unique:
      "One digital identity works across every campus system — transit, cafeteria, library, and exams — for life.",
    stats: [
      { value: "1.2M+", label: "Verified identities" },
      { value: "19", label: "Identity types" },
      { value: "100%", label: "SSO coverage" },
      { value: "0", label: "Duplicate records" },
    ],
    visuals: { type: "identity" },
  },
  {
    id: "learn",
    name: "MOKA Learn",
    shortName: "Learn",
    category: "Learning",
    tagline: "Every lesson. Every assignment. Every achievement.",
    description:
      "MOKA Learn is the digital learning operating system that manages curriculum delivery, classroom engagement, assessments, personalized learning, and academic progress from enrollment through graduation. It provides educators with powerful instructional tools while giving students and parents complete visibility into learning outcomes.",
    icon: BookIcon,
    color: GOLD,
    gradient: "linear-gradient(135deg, #B8893C, #F4C542)",
    capabilities: [
      "Curriculum Management",
      "Lesson Planning",
      "Timetabling",
      "Digital Classrooms",
      "Live & Recorded Learning",
      "Assignments",
      "Homework Tracking",
      "Assessment & Exams",
      "Continuous Assessment",
      "Grading & Report Cards",
      "Competency Tracking",
      "Personalized Learning Paths",
      "AI Tutor",
      "AI Study Assistant",
      "Learning Analytics",
      "Attendance Integration",
      "Parent Progress Portal",
      "Teacher Collaboration",
      "Resource Library",
      "LMS Integrations",
    ],
    supports: [
      "Competency-based learning",
      "National curriculum alignment",
      "Hybrid learning",
      "Remote learning",
      "AI-powered interventions",
      "Learning recommendations",
    ],
    metric: { value: "50K+", label: "Active learners" },
    unique:
      "Learning records feed directly into the parent dashboard — no separate app, no separate login.",
    stats: [
      { value: "50K+", label: "Active learners" },
      { value: "1.8M", label: "Grade entries" },
      { value: "340+", label: "Curriculum subjects" },
      { value: "94%", label: "Assignment completion" },
    ],
    visuals: { type: "learn" },
  },
  {
    id: "transit",
    name: "MOKA Transit",
    shortName: "Transit",
    category: "Transport",
    tagline: "Every bus. Every route. Every child.",
    description:
      "MOKA Transit digitizes school transportation, providing real-time visibility into fleets, drivers, routes, and student journeys. Parents know exactly where their children are, schools optimize operations, and transport providers gain powerful logistics tools.",
    icon: TransportIcon,
    color: GREEN,
    gradient: "linear-gradient(135deg, #0E3B36, #238B3F)",
    capabilities: [
      "Fleet Management",
      "Route Planning",
      "Route Optimization",
      "GPS Tracking",
      "Driver Management",
      "Student Boarding",
      "Student Drop-off Verification",
      "QR Boarding",
      "NFC Boarding",
      "Parent Live Tracking",
      "ETA Prediction",
      "Geofencing",
      "Driver Behaviour Monitoring",
      "Fuel Monitoring",
      "Vehicle Maintenance",
      "Incident Reporting",
      "Emergency Alerts",
      "Offline Operation",
      "Fleet Analytics",
      "AI Route Optimization",
    ],
    knows: [
      "who boarded",
      "who missed the bus",
      "current location",
      "estimated arrival",
      "delays",
      "route deviations",
      "emergencies",
    ],
    metric: { value: "3s", label: "GPS update interval" },
    unique:
      "Geofencing alerts parents the moment their child boards and exits the bus — automatically.",
    stats: [
      { value: "96", label: "Buses tracked" },
      { value: "3s", label: "GPS interval" },
      { value: "920K", label: "GPS pings/day" },
      { value: "99.7%", label: "On-time arrival" },
    ],
    visuals: { type: "transit" },
  },
  {
    id: "connect",
    name: "MOKA Connect",
    shortName: "Connect",
    category: "Communication",
    tagline: "Every conversation. One trusted channel.",
    description:
      "MOKA Connect unifies all school communication into a single, secure platform, replacing fragmented SMS, WhatsApp groups, paper notices, and email chains with structured, trackable communication between schools, families, and communities.",
    icon: MessageSquareIcon,
    color: GOLD,
    gradient: "linear-gradient(135deg, #B8893C, #FFD66B)",
    capabilities: [
      "Parent Messaging",
      "Teacher Messaging",
      "Broadcast Announcements",
      "Emergency Notifications",
      "SMS",
      "WhatsApp Integration",
      "Email",
      "Push Notifications",
      "Voice Broadcasts",
      "Digital Circulars",
      "Event Invitations",
      "Parent Conferences",
      "School Calendar",
      "Surveys & Polls",
      "Two-way Communication",
      "Translation",
      "Message Read Receipts",
      "Communication Analytics",
      "AI Message Assistant",
      "Community Groups",
    ],
    metric: { value: "99.2%", label: "Delivery rate" },
    unique:
      "Auto-translates messages between English and local languages — bridging the communication gap.",
    stats: [
      { value: "99.2%", label: "Delivery rate" },
      { value: "8", label: "Channels unified" },
      { value: "12", label: "Languages supported" },
      { value: "2.4M", label: "Messages/month" },
    ],
    visuals: { type: "connect" },
  },
  {
    id: "campus",
    name: "MOKA Campus",
    shortName: "Campus",
    category: "Campus OS",
    tagline: "The digital operating system for every moment of school life.",
    description:
      "MOKA Campus is the operating system that runs an entire school every minute of every day, from the moment the first bus leaves in the morning until the last security guard locks the gate. It unifies every operational experience into one intelligent platform, creating a living digital twin of the entire institution where every person, place, asset, event and transaction is connected and visible in real time.",
    icon: MealIcon,
    color: GREEN,
    gradient: "linear-gradient(135deg, #0E3B36, #238B3F)",
    capabilities: [
      "Living Campus Dashboard",
      "Student Digital Profiles",
      "Parent & Guardian Management",
      "Teacher & Staff Workflows",
      "Support Staff Operations",
      "Attendance & Roll Call",
      "Visitor Management",
      "National ID Verification",
      "Student Health Records",
      "Nurse Clinic Operations",
      "Triage & Treatment Plans",
      "Medication Dispensing",
      "Disease Outbreak Monitoring",
      "Vaccination Tracking",
      "Mental Wellbeing Referrals",
      "Intelligent Cafeteria POS",
      "Allergy Safety Checks",
      "Nutrition & Meal Planning",
      "Kitchen Intelligence",
      "Food Waste Monitoring",
      "Boarding Management",
      "Dormitory Roll Calls",
      "Leave Requests",
      "Dorm Inspections",
      "Lights-Out Compliance",
      "Library Management",
      "Book Loans & Returns",
      "Sports Facility Booking",
      "Clubs & Societies",
      "Student Discipline",
      "Behaviour Tracking",
      "Wellbeing Indicators",
      "Security Operations",
      "Gate & Vehicle Control",
      "QR Visitor Badges",
      "Student Pickup Verification",
      "Emergency Lockdown",
      "Mustering & Incident Reporting",
      "Campus Commerce & Wallets",
      "Parent Spending Controls",
      "Facilities & Maintenance",
      "Work Order Management",
      "Asset Tracking",
      "Preventive Maintenance",
      "Smart Student Journey",
      "Campus Intelligence & Prediction",
    ],
    metric: { value: "2,481", label: "Students on campus now" },
    unique:
      "The cafeteria POS cross-checks student identity, meal entitlement, wallet balance, boarding schedule, food allergies, medical restrictions, daily nutrition goals, and parent restrictions before every purchase — blocking unsafe meals automatically and suggesting safe alternatives.",
    stats: [
      { value: "2,481", label: "Students on campus" },
      { value: "173", label: "Staff present" },
      { value: "742", label: "Meals served today" },
      { value: "96%", label: "Attendance" },
    ],
    visuals: { type: "campus" },
  },
  {
    id: "intelligence",
    name: "MOKA Intelligence",
    shortName: "Intelligence",
    category: "Intelligence",
    tagline: "The intelligence layer powering every decision.",
    description:
      "MOKA Intelligence transforms operational data into predictive insights, enabling schools, parents, operators, and governments to make informed decisions using AI, analytics, forecasting, and automation.",
    icon: SparkleIcon,
    color: GOLD,
    gradient: "linear-gradient(135deg, #B8893C, #FFD66B)",
    capabilities: [
      "Executive Dashboards",
      "Student Success Prediction",
      "Dropout Risk Detection",
      "Attendance Analytics",
      "Financial Analytics",
      "Transport Analytics",
      "Learning Analytics",
      "Behaviour Analytics",
      "AI Recommendations",
      "Early Warning System",
      "Institutional Benchmarking",
      "Enrollment Forecasting",
      "Revenue Forecasting",
      "Capacity Planning",
      "Natural Language Queries",
      "AI Agents",
      "Automated Reports",
      "Operational Monitoring",
      "Performance KPIs",
      "National Education Intelligence",
    ],
    aiCanAnswer: [
      "Which students need intervention?",
      "Which routes are inefficient?",
      "Which schools are underperforming?",
      "Which parents are disengaged?",
      "Which fees are at risk?",
      "Which teachers need support?",
      "Where are operational bottlenecks?",
    ],
    metric: { value: "12+", label: "Prediction models" },
    unique:
      "Predicts which students are at risk of dropping out — weeks before they do — enabling early intervention.",
    stats: [
      { value: "12+", label: "Prediction models" },
      { value: "8.4K", label: "Events/sec" },
      { value: "1.2M", label: "Training data" },
      { value: "94%", label: "Top model accuracy" },
    ],
    visuals: { type: "intelligence" },
  },
  {
    id: "government",
    name: "MOKA Government",
    shortName: "Government",
    category: "Compliance",
    tagline: "Compliance built into every workflow.",
    description:
      "MOKA Government automates regulatory compliance, statutory reporting, inspections, and education governance, reducing administrative burden while improving transparency between institutions and regulators.",
    icon: DocumentIcon,
    color: GREEN,
    gradient: "linear-gradient(135deg, #0E3B36, #0E3B36)",
    capabilities: [
      "Ministry Reporting",
      "Regulatory Compliance",
      "School Licensing",
      "Inspection Management",
      "Digital Audits",
      "Policy Management",
      "Accreditation",
      "EMIS Integration",
      "National Student Registry",
      "Teacher Registry",
      "Financial Reporting",
      "Safeguarding Compliance",
      "Child Protection",
      "Data Privacy",
      "Record Retention",
      "Automated Regulatory Reports",
      "Compliance Monitoring",
      "Risk Scoring",
      "Government Dashboards",
      "Multi-jurisdiction Support",
    ],
    metric: { value: "100%", label: "Compliance rate" },
    unique:
      "Generates and submits government compliance reports automatically each term — no manual compilation.",
    stats: [
      { value: "100%", label: "Compliance rate" },
      { value: "20", label: "Regulatory modules" },
      { value: "5", label: "Jurisdictions" },
      { value: "0", label: "Manual reports" },
    ],
    visuals: { type: "government" },
  },
  {
    id: "pay",
    name: "MOKA Pay",
    shortName: "Pay",
    category: "Payments",
    tagline: "Every payment. Every collection. One platform.",
    description:
      "MOKA Pay is the financial operating system for education, orchestrating tuition, transport, meals, uniforms, books, fundraising, scholarships, payroll, and supplier payments through a unified payment infrastructure. It is designed to abstract away payment providers so institutions can accept any payment method while families enjoy a seamless experience.",
    icon: ReceiptIcon,
    color: GOLD,
    gradient: "linear-gradient(135deg, #B8893C, #F4C542)",
    capabilities: [
      "Tuition Collection",
      "Transport Payments",
      "Cafeteria Wallet",
      "School Store Payments",
      "Uniform & Book Payments",
      "Merchant Marketplace",
      "Scholarships & Sponsorships",
      "Bursary Management",
      "Fee Plans",
      "Installment Payments",
      "Auto-Debit & Recurring Payments",
      "Digital Invoicing",
      "Payment Links",
      "QR Payments",
      "Mobile Money Integration",
      "Bank Transfers",
      "Card Payments",
      "Digital Wallets",
      "Payroll",
      "Supplier Payments",
      "Financial Reconciliation",
      "Refund Management",
      "Escrow",
      "Financial Aid Disbursement",
      "AI Collections & Payment Predictions",
    ],
    principles: [
      "One payment API regardless of provider.",
      "Intelligent routing to the lowest-cost payment rail available.",
      "Real-time reconciliation with institutional finance systems.",
      "Support for parents paying through mobile money, banks, cards, employer payroll deductions, scholarships, or sponsors.",
      "Embedded financial intelligence for forecasting, collections, and affordability planning.",
    ],
    metric: { value: "6+", label: "Payment methods" },
    unique:
      "Sub-wallets let parents restrict funds to specific purposes — meals only, books only, transport only.",
    stats: [
      { value: "R 2.8B", label: "Processed today" },
      { value: "6+", label: "Payment methods" },
      { value: "25", label: "Financial modules" },
      { value: "99.9%", label: "Reconciliation rate" },
    ],
    visuals: { type: "pay" },
  },
];

export function getProductById(id: string): ProductDetail | undefined {
  return products.find((p) => p.id === id);
}
